import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../lib/api.js";
import type { ContentType, ModelRoute, StatsRowWithScore } from "../lib/types.js";

const CONTENT_TYPES: ContentType[] = ["text", "image", "svg", "audio", "video"];

function pct(numerator: number, denominator: number): string {
  return denominator > 0 ? `${Math.round((numerator / denominator) * 100)}%` : "-";
}

function money(total: number, jobs: number): string {
  return jobs > 0 ? `$${(total / jobs).toFixed(4)}` : "-";
}

function seconds(totalMs: number, jobs: number): string {
  return jobs > 0 ? `${(totalMs / jobs / 1000).toFixed(1)}s` : "-";
}

/** Per-route generation statistics (D-24). Rows are sorted best-first by the
 *  same score `GET /api/recommend` ranks on, so the top row of each group is
 *  the route the system reaches for first. */
export function StatsPage() {
  const [stats, setStats] = useState<StatsRowWithScore[]>([]);
  const [routes, setRoutes] = useState<ModelRoute[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [s, r] = await Promise.all([
          api<{ items: StatsRowWithScore[] }>("/api/stats"),
          api<{ items: ModelRoute[] }>("/api/routes"),
        ]);
        setStats(s.items);
        setRoutes(r.items);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return;
        setError(err instanceof Error ? err.message : "Could not load statistics.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const nameOf = useMemo(() => {
    const map = new Map(routes.map((r) => [r.routeId, r.displayName] as const));
    return (routeId: string) => map.get(routeId) ?? routeId;
  }, [routes]);

  if (loading) return <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>;
  if (error) return <p className="text-sm text-red-600 dark:text-red-400">{error}</p>;
  if (stats.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        No statistics yet - generate and rate some outputs first.
      </p>
    );
  }

  return (
    <div>
      {CONTENT_TYPES.map((ct) => {
        const group = stats
          .filter((s) => s.contentType === ct)
          .sort((a, b) => b.score - a.score);
        if (group.length === 0) return null;
        return (
          <section key={ct} className="mt-6 first:mt-0">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {ct}
            </h2>
            <div className="mt-2 overflow-x-auto rounded-md border border-gray-200 dark:border-gray-800">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                  <tr>
                    <th className="px-3 py-2">Route</th>
                    <th className="px-3 py-2">Jobs</th>
                    <th className="px-3 py-2">Success</th>
                    <th className="px-3 py-2">Up</th>
                    <th className="px-3 py-2">Chosen</th>
                    <th className="px-3 py-2">Avg $/job</th>
                    <th className="px-3 py-2">Avg time</th>
                    <th className="px-3 py-2">Score</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {group.map((s, i) => (
                    <tr key={s.routeId}>
                      <td className="px-3 py-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {nameOf(s.routeId)}
                        </span>
                        {i === 0 && (
                          <span className="ml-2 rounded bg-(--accent) px-1.5 py-0.5 text-xs text-(--accent-contrast)">
                            Recommended first
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">{s.jobs}</td>
                      <td className="px-3 py-2">{pct(s.succeeded, s.jobs)}</td>
                      <td className="px-3 py-2">{pct(s.up, s.up + s.down)}</td>
                      <td className="px-3 py-2">{pct(s.chosen, s.chosen + s.notChosen)}</td>
                      <td className="px-3 py-2">{money(s.usdTotal, s.jobs)}</td>
                      <td className="px-3 py-2">{seconds(s.durationMsTotal, s.jobs)}</td>
                      <td className="px-3 py-2">{s.score.toFixed(3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </div>
  );
}
